import { z } from 'zod';
import { PAIRING_CODE_ALPHABET, PAIRING_CODE_LENGTH } from '../constants.js';
import { Capabilities, DeviceName, DeviceType, Id, IsoDateTime, PlatformInfo } from './common.js';

/** POST /pairing/code (dashboard). */
export const PairingCodeResponse = z.object({
  code: z.string(),
  expiresAt: IsoDateTime,
  /** `findmy://pair?server=…&code=…`, shown as a QR code for the agent to scan. */
  qrPayload: z.string(),
});
export type PairingCodeResponse = z.infer<typeof PairingCodeResponse>;

const codePattern = new RegExp(`^[${PAIRING_CODE_ALPHABET}]{${PAIRING_CODE_LENGTH}}$`);

/** Accepts what a person might type: lower case, spaces or a dash in the middle. */
export const PairingCode = z
  .string()
  .max(20)
  .transform((code) => code.toUpperCase().replace(/[\s-]/g, ''))
  .pipe(z.string().regex(codePattern, 'That is not a valid pairing code.'));

/** POST /devices/pair (agent, no auth other than the code). */
export const PairRequest = z.object({
  code: PairingCode,
  name: DeviceName,
  type: DeviceType,
  platformInfo: PlatformInfo.default({}),
  capabilities: Capabilities.default([]),
  agentVersion: z.string().max(40).optional(),
});
export type PairRequest = z.input<typeof PairRequest>;

/** Server-recommended agent settings, sent once at pairing. */
export const DeviceConfig = z.object({
  reportIntervalSec: z.number().int().positive(),
  maxBatchSize: z.number().int().positive(),
});
export type DeviceConfig = z.infer<typeof DeviceConfig>;

export const PairResponse = z.object({
  deviceId: Id,
  /** Shown once. The server keeps only its SHA-256 hash. */
  token: z.string(),
  config: DeviceConfig,
});
export type PairResponse = z.infer<typeof PairResponse>;
